import { ColumnInstance } from "react-table";
import { TableRow } from "./types";
import { useMemo } from "react";
import Select, { components, OptionProps, SingleValue } from "react-select";

/** @deprecated */
export function MultiSelectFilter({
  column,
  labelTransform,
}: {
  column: ColumnInstance<TableRow>;
  labelTransform?: "timestamp_to_date" | "none";
}): JSX.Element {
  const { filterValue, preFilteredRows, setFilter, id, Header } = column;

  const getLabel = (value: string | number): string | number => {
    switch (labelTransform) {
      case "timestamp_to_date":
        return new Date((value as number) * 1000).toLocaleDateString("en-US", {
          timeZone: "America/New_York",
        });
      case "none":
        return value;
      default:
        return value;
    }
  };

  // Calculate the options for filtering
  // using the preFilteredRows
  const options = useMemo(() => {
    const options: Set<string> = new Set();
    preFilteredRows.forEach((rowz: any) => {
      if (typeof rowz.values[id] !== "undefined") {
        options.add(rowz.values[id]);
      }
    });
    return Array.from(options).map((x) => ({ value: x, label: getLabel(x) }));
  }, [id, preFilteredRows]);

  // Render a multi-select box
  return (
    <Select
      options={options}
      isMulti
      name={`${Header}`}
      // display selected options in Select box, whether new or pre-selected
      value={
        Array.isArray(filterValue)
          ? options.filter(
              (option: { value: string | number; label: string | number }) => {
                return filterValue.includes(option.value);
              }
            )
          : []
      }
      hideSelectedOptions={false}
      menuPortalTarget={
        typeof document !== "undefined" ? document.body : undefined
      }
      closeMenuOnSelect={true}
      styles={{
        menu: (provided: any) => ({
          ...provided,
          color: "black",
          transition: "opacity 0.5s",
        }),
        input: (provided: any) => ({
          ...provided,
          border: "none",
        }),
        control: (provided: any) => ({
          ...provided,
          background: "#fff",
          borderColor: "#9e9e9e",
          minHeight: "24px",
        }),
        option: (provided: any, state: any) => ({
          background: "white",
        }),
        dropdownIndicator: (provided, state) => ({
          ...provided,
          transition: "transform 0.15s", // Ensure the transition is applied
        }),
      }}
      classNames={{
        menu: (state) =>
          `text-gray-800 px-2 py-2 text-sm font-normal font-['Lato'] leading-relaxed`,
        input: () =>
          "text-gray-800 px-4 py-2 text-sm font-normal font-['Lato'] leading-relaxed ",
        dropdownIndicator: (state) =>
          `${
            state.selectProps.menuIsOpen
              ? "rotate-180 cursor-pointer"
              : "rotate-0 cursor-pointer"
          }`,
      }}
      components={{
        ValueContainer: multiCheckValueContainer,
        Option: multiCheckOption,
      }}
      onChange={(e) => {
        if (Array.isArray(e)) {
          if (e.length === 0) {
            setFilter(undefined);
          } else {
            setFilter(e.map((x) => x.value));
          }
        } else {
          const value = (e as SingleValue<Option>)?.value;
          if (typeof value === "undefined") {
            setFilter(undefined);
          } else {
            setFilter([(e as SingleValue<Option>)?.value]);
          }
        }
      }}
    />
  );
}

const multiCheckValueContainer: React.FC<any> = ({ children, ...props }) => {
  const count = props.getValue().length;
  const name = props.selectProps.name;
  return (
    <components.ValueContainer {...props}>
      <div className="m-2 w-full cursor-pointer text-gray-400" tabIndex={0}>
        {count > 0
          ? `${count} ${name}${count > 1 ? "s" : ""} Selected`
          : `Select ${name}s`}
        <div className="sr-only">{children}</div>
      </div>
    </components.ValueContainer>
  );
};

interface Option {
  value: string | number;
  label: string | number;
}
//Custom option component for multi-select filter, used in multiCheckFilter
const multiCheckOption: React.FC<OptionProps<Option>> = (props) => {
  return (
    <components.Option {...props}>
      <div className="flex items-center px-4 py-2">
        <div className="mr-4 flex items-center">
          <div className="relative h-4 w-full hover:cursor-pointer">
            <div
              className={`absolute left-0 top-0 h-4 w-4 border-2 bg-white ${
                props.isSelected ? "border-blue-600" : "border-slate-400"
              }`}
            />
            {props.isSelected && (
              <div className="absolute left-[4px] top-[4px] h-2 w-2 border-2 border-blue-600 bg-blue-600" />
            )}
          </div>
        </div>
        <span className="ml-2 text-gray-800 hover:cursor-pointer">
          {props.label}
        </span>
      </div>
    </components.Option>
  );
};
